/**
 * Attempt to fetch a remote image URL and convert it to a data URL.
 *
 * Why: html-to-image needs canvas-accessible images for PNG export. Remote
 * images only work if the source server returns CORS headers. Many CDNs
 * (premierleague.com, club official sites, etc.) don't. Embedding as a
 * data URL bypasses CORS entirely — the image becomes part of the document.
 *
 * Returns:
 *   { success: true, dataUrl }   — embed worked, replace URL with dataUrl
 *   { success: false, reason }   — embed failed (CORS-blocked, 404, etc.)
 *
 * Use on blur of any image-URL input so the user doesn't have to manually
 * download + upload for CORS-friendly sources.
 */
export type EmbedResult =
  | { success: true; dataUrl: string }
  | { success: false; reason: string };

export async function autoEmbedImage(url: string): Promise<EmbedResult> {
  if (!url) return { success: false, reason: "empty" };
  if (url.startsWith("data:")) {
    // Already a data URL — no work needed
    return { success: true, dataUrl: url };
  }

  try {
    // mode: "cors" is the default for fetch from a different origin.
    // If the server doesn't return Access-Control-Allow-Origin, this throws.
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) {
      return { success: false, reason: `HTTP ${res.status}` };
    }
    const blob = await res.blob();
    if (!blob.type.startsWith("image/")) {
      return { success: false, reason: `not an image (${blob.type})` };
    }
    const dataUrl = await blobToDataUrl(blob);
    return { success: true, dataUrl };
  } catch (err) {
    // Most common: TypeError "Failed to fetch" — CORS or network failure
    const reason = err instanceof Error ? err.message : "fetch failed";
    return { success: false, reason };
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("FileReader failed"));
    reader.readAsDataURL(blob);
  });
}
